import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

export default class extends Controller {
  static targets = ["budgetTotal", "retainageModal", "retainageInput", "cfsiInput", "cfisVsCommitted", "cfisVsCommittedCell", "cfisCard", "cfisBadge", "cfisVariancePct", "cfisStatusDivider", "cfisStatusText"]

  static values = {
    projectId: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    overallAca: Number
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._showPendingFlash()
    track("Finance Dashboard Viewed", { project_id: this.projectIdValue })
  }

  // ── Number/currency helpers ───────────────────────────────────────

  _parseNumber(str) {
    if (!str) return 0
    let cleaned = str.trim()
    // Strip currency symbol(s) from start
    cleaned = cleaned.replace(/^[^\d\-]+/, "")
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned)
    return isNaN(val) ? 0 : val
  }

  _formatNumber(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    if (this.currencyFormatValue === "european_currency_format") {
      return abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      return abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      return abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
  }

  _formatCurrency(num) {
    const formatted = this._formatNumber(num)
    return formatted ? `${this.currencyValue}${formatted}` : ""
  }

  // ── Budget editing ────────────────────────────────────────────────

  updateBudget(event) {
    const el = event.target
    const budgetType = el.dataset.budgetType
    const amount = this._parseNumber(el.textContent)
    el.textContent = this.currencyValue + this._formatNumber(amount)

    this._ajax("POST", "update_dashboard_budgets", { budget_type: budgetType, amount })
      .then(() => {
        this._recalcBudgetTotal()
        this._showToast("Budget updated")
      })
      .catch(() => this._showToast("Failed to update budget", "error"))
  }

  _recalcBudgetTotal() {
    let total = 0
    this.element.querySelectorAll("[data-budget-type]").forEach(cell => {
      total += this._parseNumber(cell.textContent)
    })
    if (this.hasBudgetTotalTarget) {
      this.budgetTotalTarget.textContent = this.currencyValue + this._formatNumber(total)
    }
  }

  // ── Retainage modal ───────────────────────────────────────────────

  showRetainageModal() {
    this.retainageModalTarget.classList.remove("hidden")
  }

  hideRetainageModal() {
    this.retainageModalTarget.classList.add("hidden")
  }

  updateRetainage() {
    const val = parseFloat(this.retainageInputTarget.value)
    if (isNaN(val) || val < 0 || val > 100) {
      this._showToast("Please enter a valid percentage (0-100)", "error")
      return
    }
    this._ajax("PUT", "update_retainage", { retainage: val })
      .then(() => {
        this.hideRetainageModal()
        this._flashAndReload("Retainage updated")
      })
      .catch(() => this._showToast("Failed to update retainage", "error"))
  }

  // ── CFSI Approved Funds (inline edit) ─────────────────────────────

  saveCfsiFunds() {
    const val = this._parseNumber(this.cfsiInputTarget.value)
    this.cfsiInputTarget.value = this._formatNumber(val)

    const overallAca = this.overallAcaValue
    const diff = val - overallAca
    const absDiff = Math.abs(diff)
    const isOver = diff < 0
    const variancePct = val > 0 ? ((absDiff / val) * 100).toFixed(1) : 0
    const cur = this.currencyValue

    // Variance amount — rebuild the entire variance line
    if (this.hasCfisVsCommittedTarget) {
      const wrapper = this.cfisVsCommittedTarget.parentElement
      if (wrapper) {
        this.cfisVsCommittedTarget.textContent = this._formatNumber(absDiff)
        // Replace everything before the inner span with the sign+currency prefix
        const prefix = `${isOver ? '-' : '+'}${cur}`
        // Remove all text nodes before the target span
        while (wrapper.firstChild && wrapper.firstChild !== this.cfisVsCommittedTarget) {
          wrapper.removeChild(wrapper.firstChild)
        }
        wrapper.insertBefore(document.createTextNode(prefix), this.cfisVsCommittedTarget)
      }
    }

    // Variance color
    if (this.hasCfisVsCommittedCellTarget) {
      this.cfisVsCommittedCellTarget.classList.remove("text-green-600", "text-red-600")
      this.cfisVsCommittedCellTarget.classList.add(isOver ? "text-red-600" : "text-green-600")
    }

    // Variance percentage
    if (this.hasCfisVariancePctTarget) {
      this.cfisVariancePctTarget.textContent = val > 0 ? `${variancePct}% ${isOver ? 'over budget' : 'under budget'}` : ''
    }

    // Card border
    if (this.hasCfisCardTarget) {
      this.cfisCardTarget.classList.remove("border-red-200", "border-gray-200")
      this.cfisCardTarget.classList.add(isOver ? "border-red-200" : "border-gray-200")
    }

    // Badge
    if (this.hasCfisBadgeTarget) {
      this.cfisBadgeTarget.classList.remove("bg-red-50", "text-red-600", "border-red-200", "bg-green-50", "text-green-700", "border-green-200")
      this.cfisBadgeTarget.classList.add(...(isOver ? ["bg-red-50", "text-red-600", "border-red-200"] : ["bg-green-50", "text-green-700", "border-green-200"]))
    }

    // Status divider border
    if (this.hasCfisStatusDividerTarget) {
      this.cfisStatusDividerTarget.classList.remove("border-red-200", "border-gray-200")
      this.cfisStatusDividerTarget.classList.add(isOver ? "border-red-200" : "border-gray-200")

      // Update icon + message
      const icon = this.cfisStatusDividerTarget.querySelector("svg")
      if (icon) {
        icon.classList.remove("text-red-400", "text-green-500")
        icon.classList.add(isOver ? "text-red-400" : "text-green-500")
        icon.innerHTML = isOver
          ? '<path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />'
          : '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />'
      }
    }

    if (this.hasCfisStatusTextTarget) {
      this.cfisStatusTextTarget.classList.remove("text-red-600", "text-green-700")
      this.cfisStatusTextTarget.classList.add(isOver ? "text-red-600" : "text-green-700")
      this.cfisStatusTextTarget.innerHTML = isOver
        ? `Committed funds exceed approved budget by <strong>${cur}${this._formatNumber(absDiff)}</strong>. Update the approved budget or reduce commitments.`
        : `Project is within approved budget — <strong>${cur}${this._formatNumber(absDiff)}</strong> remaining headroom.`
    }

    this._ajax("PUT", "update_cfsi_funds", { amount: val })
      .then(() => this._showToast("Approved Funds updated"))
      .catch(() => this._showToast("Failed to update approved funds", "error"))
  }

  // ── Event helpers ─────────────────────────────────────────────────

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Toast helpers ─────────────────────────────────────────────────

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("finance_dashboard_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("finance_dashboard_flash")
    if (!raw) return
    sessionStorage.removeItem("finance_dashboard_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── AJAX helper ───────────────────────────────────────────────────

  async _ajax(method, action, params = {}) {
    const url = `/projects/${this.projectIdValue}/finances/${action}`
    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => body.append(k, v))

    const options = {
      method: method === "GET" ? "GET" : "POST",
      headers: { "X-CSRF-Token": this._csrfToken },
    }

    if (method !== "GET") {
      if (method !== "POST") body.append("_method", method.toLowerCase())
      options.body = body
    }

    try {
      const resp = await fetch(url, options)
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return resp.ok
    } catch (e) {
      console.error("Finance dashboard request failed:", e)
    }
  }
}
